import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import { Button, ButtonProps, CircularProgress, Stack } from "@mui/material";
import { AsyncContent, RoleComponentGuard, Search } from "components";
import { AccessDenied } from "components/Access-denied/AccessDenied";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { Filters } from "components/Task-manage/Filters/Filters";
import { Table } from "components/Task-manage/Table/Table";
import "components/Task-manage/TaskManage.styles.scss";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { useActionHook, useDidUpdate, useNavigate } from "hooks";
import { debounce } from "lodash";
import {
  useClientActions,
  useClientIdSelector,
  useCreateTaskSelect,
  useTaskManageActions,
  useUserRoleSelector
} from "store";
import { TaskQueries } from "store/types";
import { IClientTaskFilters } from "store/types/client";

import EmptyCardTasks from "../../../assets/images/EmptyCardTasks.png";
import { RoleName } from "../../../constants";

const buttonStyles: ButtonProps["sx"] = {
  height: "27px",
  fontSize: "16px",
  textTransform: "none",
  fontWeight: "400"
};

type TaskFilterState = Pick<TaskQueries, "deal" | "status" | "assignee" | "due_date">;
const EMPTY_FILTERS: TaskFilterState = { deal: [], status: [], assignee: [], due_date: [] };

export const TasksTab: React.FC = () => {
  const clientId = useClientIdSelector();
  const roleName = useUserRoleSelector()?.name ?? null;
  const navigate = useNavigate();
  const canManage = roleName === RoleName.Admin || roleName === RoleName.Analyst;

  const { getClientTasksAction } = useClientActions();
  const { setCreateAction } = useTaskManageActions();
  const createState = useCreateTaskSelect();

  const loadingOpts = useMemo(() => ({ defaultLoadingState: true }), []);
  const { data: tasks, loading, action: getClientTasks } = useActionHook(getClientTasksAction, loadingOpts);

  const isInitialLoadStarted = useRef(false);
  const [taskFilters, setTaskFilters] = useState<TaskFilterState>(EMPTY_FILTERS);
  const [searchInput, setSearchInput] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");

  const onSearchDebounce = useMemo(() => debounce((value: string) => setDebouncedSearch(value.trim()), 500), []);
  useEffect(() => () => onSearchDebounce.cancel(), [onSearchDebounce]);

  const updateTaskFilters = useCallback((data: Partial<TaskQueries>) => {
    setTaskFilters((prev) => ({ ...prev, ...data }));
  }, []);

  const filters = useMemo<IClientTaskFilters>(
    () => ({
      status: taskFilters.status.length ? taskFilters.status.map((item) => item.value) : undefined,
      assignee_id: taskFilters.assignee.length ? taskFilters.assignee[0].id : undefined,
      search: debouncedSearch || undefined
    }),
    [taskFilters.status, taskFilters.assignee, debouncedSearch]
  );

  const fetchTasks = useCallback(() => {
    if (!clientId) return;
    getClientTasks(clientId, filters);
  }, [clientId, filters]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    if (loading) {
      isInitialLoadStarted.current = true;
    }
  }, [loading]);

  // Refetch once the shared task-create modal closes (a client task may have been added).
  useDidUpdate(() => {
    if (!createState.isOpen) {
      fetchTasks();
    }
  }, [createState.isOpen]);

  // The client /tasks endpoint returns the same TaskPreview shape as the deal Table expects.
  const taskList = tasks ?? [];

  const hasActiveFilters = Boolean(taskFilters.status.length || taskFilters.assignee.length || debouncedSearch);

  const handleAddTask = useCallback(() => {
    if (!clientId) return;
    setCreateAction({ isOpen: true, disableRedirectOnSubmit: true, entity_type: "client", entity_id: clientId });
  }, [clientId, setCreateAction]);

  return (
    <RoleComponentGuard
      availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
      RenderInstead={<AccessDenied redirectClick={() => navigate({ route: "main" })} title="Back home" />}
    >
      {taskList.length > 0 || hasActiveFilters ? (
        <div className="task-manage" style={{ margin: "0" }}>
          <div className="head">
            <div className="filter-block">
              <Filters
                updateQueries={updateTaskFilters}
                deal={taskFilters.deal}
                status={taskFilters.status}
                due_date={taskFilters.due_date}
                assignee={taskFilters.assignee}
                triggerStyles={buttonStyles}
              />
            </div>
            <div className="rest">
              <Search
                defaultValue={searchInput}
                onChange={(event) => {
                  setSearchInput(event.target.value);
                  onSearchDebounce(event.target.value);
                }}
                placeholder="Search"
                className="search"
              />
            </div>
          </div>
          <AsyncContent spinnerProps={{ size: 150 }} loading={loading} className="content">
            <Table refetchTable={fetchTasks} tasks={taskList} />
          </AsyncContent>
        </div>
      ) : loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <EmptyCard
          title={EMPTY_CARD_CONTENT.taskManage.title}
          description={{
            text: EMPTY_CARD_CONTENT.taskManage.description,
            externalText: EMPTY_CARD_CONTENT.taskManage.externalDescription
          }}
          image={{ src: EmptyCardTasks, sx: { width: 315, height: 125 } }}
          fitPage
          actionsElement={
            canManage && (
              <Button variant="contained" onClick={handleAddTask} startIcon={<AddIcon />} sx={{ bgcolor: "#071F35" }}>
                Add Task
              </Button>
            )
          }
        />
      )}
    </RoleComponentGuard>
  );
};

export default TasksTab;
